import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useSettings, DEFAULT_APPEARANCE, DEFAULT_RUNNING_BANNER } from '@/context/SettingsContext';
import { ADMIN } from '@/constants/testIds';
import AppearancePanel from './AppearancePanel';
import ImageInputField from '@/components/admin/ImageInputField';

const TABS = [
  { id: 'store', label: 'Store & Delivery' },
  { id: 'appearance', label: 'Appearance & Banner' },
  { id: 'password', label: 'Change Password' },
];

export default function AdminSettings() {
  const { refresh } = useSettings();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState('store');

  useEffect(() => {
    api.get('/settings').then((r) => {
      const data = r.data || {};
      setForm({
        ...data,
        appearance: { ...DEFAULT_APPEARANCE, ...(data.appearance || {}) },
        running_banner: { ...DEFAULT_RUNNING_BANNER, ...(data.running_banner || {}) },
      });
    });
  }, []);

  const set = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : (e.target.type === 'number' ? parseFloat(e.target.value || 0) : e.target.value);
    setForm({ ...form, [k]: v });
  };

  const setPincodes = (text) => {
    const list = text.split(/[\s,;\n]+/).map((s) => s.trim()).filter(Boolean);
    setForm({ ...form, serviceable_pincodes: list });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put('/settings', form);
      await refresh();
      toast.success('Settings saved — applied site-wide');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  if (!form) return <p className="text-sm text-stone-500">Loading…</p>;
  const inputCls = 'w-full h-11 px-3 rounded-xl bg-white border border-stone-200 focus:border-olive-400 focus:outline-none text-sm';

  return (
    <div className="space-y-5">
      <h1 className="font-heading text-3xl text-stone-900">Settings</h1>
      <div className="flex flex-wrap gap-2 border-b border-stone-200">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            data-testid={`admin-settings-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.id ? 'border-fresh-600 text-fresh-700' : 'border-transparent text-stone-500 hover:text-stone-900'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'store' && (
        <form onSubmit={submit} className="bg-white border border-stone-200 rounded-3xl p-5 sm:p-7 grid sm:grid-cols-2 gap-4 max-w-3xl">
          <div>
            <label className="text-xs font-medium text-stone-600">Delivery charge (₹)</label>
            <input data-testid={ADMIN.settingsDeliveryCharge} type="number" step="1" value={form.delivery_charge} onChange={set('delivery_charge')} className={inputCls}/>
          </div>
          <div>
            <label className="text-xs font-medium text-stone-600">Min. order value (₹)</label>
            <input data-testid={ADMIN.settingsMinOrderValue} type="number" step="1" value={form.min_order_value} onChange={set('min_order_value')} className={inputCls}/>
          </div>
          <div>
            <label className="text-xs font-medium text-stone-600">Free delivery above (₹)</label>
            <input data-testid={ADMIN.settingsFreeDeliveryAbove} type="number" step="1" value={form.free_delivery_above} onChange={set('free_delivery_above')} className={inputCls}/>
          </div>
          <div>
            <label className="text-xs font-medium text-stone-600">WhatsApp number (no +)</label>
            <input data-testid={ADMIN.settingsWhatsapp} value={form.whatsapp_number} onChange={set('whatsapp_number')} className={inputCls} placeholder="919319242617"/>
          </div>
          <div>
            <label className="text-xs font-medium text-stone-600">Store phone</label>
            <input data-testid={ADMIN.settingsStorePhone} value={form.store_phone} onChange={set('store_phone')} className={inputCls}/>
          </div>
          <div>
            <label className="text-xs font-medium text-stone-600">Store name</label>
            <input value={form.store_name} onChange={set('store_name')} className={inputCls}/>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-medium text-stone-600">Store address</label>
            <input value={form.store_address || ''} onChange={set('store_address')} className={inputCls}/>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-medium text-stone-600">Delivery time text</label>
            <input value={form.delivery_time_text} onChange={set('delivery_time_text')} className={inputCls}/>
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.is_store_open} onChange={set('is_store_open')}/> Store is open</label>

          <div className="sm:col-span-2 border-t border-stone-100 pt-4 mt-2">
            <h2 className="font-heading text-lg text-stone-900">Delivery Service Area</h2>
            <p className="text-xs text-stone-500 mt-0.5">Only customers whose PIN code matches the list below can place orders. Add or remove PIN codes anytime.</p>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-medium text-stone-600">Service area label</label>
            <input data-testid="admin-settings-service-area-label" value={form.service_area_label || ''} onChange={set('service_area_label')} className={inputCls} placeholder="Faridabad"/>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-medium text-stone-600">Serviceable PIN codes</label>
            <textarea
              data-testid="admin-settings-pincodes"
              rows={4}
              value={(form.serviceable_pincodes || []).join(', ')}
              onChange={(e) => setPincodes(e.target.value)}
              className="w-full px-3 py-2 rounded-xl bg-white border border-stone-200 focus:border-olive-400 focus:outline-none text-sm font-mono"
              placeholder="121001, 121002, 121003 ..."
            />
            <p className="text-xs text-stone-500 mt-1">Comma, space or newline separated. Invalid entries (non 6-digit) are dropped on save.</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {(form.serviceable_pincodes || []).map((p) => (
                <span key={p} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-olive-50 text-olive-800 border border-olive-100">
                  {p}
                  <button type="button" onClick={() => setForm({ ...form, serviceable_pincodes: form.serviceable_pincodes.filter((x) => x !== p) })} className="text-olive-700 hover:text-terracotta-700">×</button>
                </span>
              ))}
              {(form.serviceable_pincodes || []).length === 0 && <span className="text-xs text-stone-400">No PIN codes — orders will not be accepted.</span>}
            </div>
          </div>

          <div className="sm:col-span-2 border-t border-stone-100 pt-4 mt-2">
            <h2 className="font-heading text-lg text-stone-900">Promotional Banner (site strip)</h2>
            <p className="text-xs text-stone-500 mt-0.5">Shown as the site-wide strip at the top of every page.</p>
          </div>
          <label className="sm:col-span-2 flex items-center gap-2 text-sm">
            <input data-testid="admin-settings-banner-enabled" type="checkbox" checked={!!form.banner_enabled} onChange={set('banner_enabled')}/>
            Enable promotional banner
          </label>
          <div className="sm:col-span-2">
            <label className="text-xs font-medium text-stone-600">Banner text</label>
            <input data-testid="admin-settings-banner-text" value={form.banner_text || ''} onChange={set('banner_text')} className={inputCls} placeholder="Fresh groceries delivered. Cash on Delivery available."/>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-medium text-stone-600">Offer text</label>
            <input data-testid="admin-settings-offer-text" value={form.offer_text || ''} onChange={set('offer_text')} className={inputCls} placeholder="Flat ₹50 OFF on your first order — use code FRESH50"/>
          </div>

          <div className="sm:col-span-2 border-t border-stone-100 pt-4 mt-2">
            <h2 className="font-heading text-lg text-stone-900">UPI Payment Details</h2>
            <p className="text-xs text-stone-500 mt-0.5">Shown to customers whenever UPI payment is required. Update or replace anytime.</p>
          </div>
          <div>
            <label className="text-xs font-medium text-stone-600">UPI ID</label>
            <input
              data-testid="admin-settings-upi-id"
              value={form.upi_id || ''}
              onChange={set('upi_id')}
              className={inputCls}
              placeholder="yourbusiness@okaxis"
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          <div>
            <ImageInputField
              label="UPI QR scanner image"
              value={form.upi_qr_url || ''}
              onChange={(v) => setForm((f) => ({ ...f, upi_qr_url: v }))}
              testIdInput="admin-settings-upi-qr-url"
              testIdUploadBtn="admin-settings-upi-qr-upload"
              folder="upi/"
              placeholder="https://... or upload"
            />
          </div>

          <div className="sm:col-span-2 flex justify-end">
            <button data-testid={ADMIN.settingsSaveBtn} disabled={busy} type="submit" className="h-11 px-6 rounded-full bg-terracotta-600 text-white font-semibold hover:bg-terracotta-700 disabled:bg-stone-300">{busy ? 'Saving…' : 'Save Settings'}</button>
          </div>
        </form>
      )}

      {tab === 'appearance' && (
        <form onSubmit={submit} className="space-y-5">
          <AppearancePanel form={form} setForm={setForm} />
          <div className="sticky bottom-4 z-10 flex justify-end">
            <button data-testid="admin-appearance-save" disabled={busy} type="submit" className="h-12 px-8 rounded-full bg-fresh-600 text-white font-semibold hover:bg-fresh-700 disabled:bg-stone-300 shadow-lg shadow-fresh-900/20">
              {busy ? 'Saving…' : 'Save & Apply Site-wide'}
            </button>
          </div>
        </form>
      )}

      {tab === 'password' && <ChangePasswordCard inputCls={inputCls} />}
    </div>
  );
}

function ChangePasswordCard({ inputCls }) {
  const [cur, setCur] = useState('');
  const [nw, setNw] = useState('');
  const [conf, setConf] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (nw.length < 8) { toast.error('New password must be at least 8 characters'); return; }
    if (nw !== conf) { toast.error('New password and confirmation do not match'); return; }
    setBusy(true);
    try {
      const { data } = await api.put('/auth/me/password', { current_password: cur, new_password: nw });
      // Backend rotates JWT on password change; swap our token so the session survives.
      if (data?.token) {
        try { localStorage.setItem('zk_token', data.token); } catch (e) {}
      }
      toast.success('Password changed');
      setCur(''); setNw(''); setConf('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Change failed');
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-stone-200 rounded-3xl p-5 sm:p-7 grid sm:grid-cols-2 gap-4 max-w-3xl">
      <div className="sm:col-span-2">
        <h2 className="font-heading text-lg text-stone-900">Change Password</h2>
        <p className="text-xs text-stone-500 mt-0.5">Use a strong password (min 8 characters).</p>
      </div>
      <div className="sm:col-span-2">
        <label className="text-xs font-medium text-stone-600">Current password</label>
        <input data-testid="admin-current-password" type="password" autoComplete="current-password" value={cur} onChange={(e) => setCur(e.target.value)} className={inputCls} required />
      </div>
      <div>
        <label className="text-xs font-medium text-stone-600">New password</label>
        <input data-testid="admin-new-password" type="password" autoComplete="new-password" value={nw} onChange={(e) => setNw(e.target.value)} className={inputCls} required />
      </div>
      <div>
        <label className="text-xs font-medium text-stone-600">Confirm new password</label>
        <input data-testid="admin-confirm-password" type="password" autoComplete="new-password" value={conf} onChange={(e) => setConf(e.target.value)} className={inputCls} required />
      </div>
      <div className="sm:col-span-2 flex justify-end">
        <button data-testid="admin-change-password-btn" disabled={busy || !cur || !nw || !conf} type="submit" className="h-11 px-6 rounded-full bg-terracotta-600 text-white font-semibold hover:bg-terracotta-700 disabled:bg-stone-300">{busy ? 'Saving…' : 'Change Password'}</button>
      </div>
    </form>
  );
}
